import { useState } from 'react';
import { createShare } from '../services/api';
import { useMetadata } from '../context/MetadataContext';
import { useGlobalError } from '../context/GlobalErrorContext';

/**
 * Creates a share code for a created puzzle.
 *
 * `sharePuzzle(puzzleData, onShareCode)` calls `onShareCode(shareCode)` on
 * success (before the loading flag clears); building the share URL is left to
 * the caller. Failures are reported through triggerError.
 *
 * @returns {{ isLoadingShare: boolean, sharePuzzle: (puzzleData: Object, onShareCode: Function) => Promise<void> }}
 */
export const useShareCreatedPuzzle = () => {
  const { getText } = useMetadata();
  const { triggerError } = useGlobalError();
  const [isLoadingShare, setIsLoadingShare] = useState(false);

  const sharePuzzle = async (puzzleData, onShareCode) => {
    if (!puzzleData || !puzzleData.puzzle_key) {
      console.error('No puzzle key found for puzzle');
      return;
    }

    try {
      setIsLoadingShare(true);
      const shareData = await createShare(puzzleData.puzzle_key);

      if (shareData && shareData.share_code) {
        onShareCode(shareData.share_code);
      } else {
        triggerError({
          message: getText('create.share_error', 'Failed to create share link. Please try again.')
        });
      }
    } catch (error) {
      console.error('Error creating share link:', error);
      triggerError({
        message: getText('create.share_error', 'Failed to create share link. Please try again.'),
        type: 'non-critical'
      });
    } finally {
      setIsLoadingShare(false);
    }
  };

  return { isLoadingShare, sharePuzzle };
};

export default useShareCreatedPuzzle;
